import { array, bool, func, node, object, string } from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type Dispatch, type PropsWithChildren, type SetStateAction } from 'react';

import { useLocalStorage } from 'hooks/useLocalStorage';
import localeConfigs from 'utils/localeConfigs';
import { COOKIES, eraseCookie, getCookie, getLang, getMarket } from 'utils/helpers';
import { AUTO_BONDS, IS_CAMPER, IS_NNORMAL } from 'utils/constants';
import { isBlank } from 'utils/StringUtils';
import { retrieveBag } from 'actions/products';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { parseBagItemsEcommerceGA4 } from 'utils/gtmUtils';
import { type ValidationString } from 'utils/validation';
import { isDomain } from 'utils/domain';

type RetrievBagHandlerArgs = {
  locale: string;
  config: Record<string, any>; // TODO: definir profileData.config
  setBag: (bag: any) => void;
  setBagLength: (length: number) => void;
  setPromoLabel: (promoLabel: string) => void;
  setPromoLabelType: (promoLabelType: string) => void;
  setBagHasMemberOnlyProduct: (hasMemberOnlyProduct: boolean) => void;
  setDisabledProducts: (disabledProducts: string) => void;
};

const retrieveBagHandler = async ({
  locale,
  config,
  setBag,
  setBagLength,
  setPromoLabel,
  setPromoLabelType,
  setBagHasMemberOnlyProduct,
  setDisabledProducts,
}: RetrievBagHandlerArgs) => {
  try {
    const lang = getLang(locale);
    const market = getMarket(locale);
    const bagHash = getCookie(COOKIES.BAG);
    const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
    const voucher = getCookie(COOKIES.BONDS) || '';
    const voucherDiscount = IS_CAMPER && autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
    const cookiesMarket = getCookie(COOKIES.BAG_MARKET);
    const showFreeShipping = config?.show_free_shipping || false;
    const showGiftPurchase = config?.show_gift_purchase || false;

    let updatedPromoLabel = null;
    let updatedPromoLabelType = 'progress';
    let currentBag = [];

    if (bagHash && cookiesMarket === market) {
      const payload: TODO = {
        hashOrderId: bagHash,
        profile: {
          countryId: market,
          languageId: lang,
        },
      };

      if (voucherDiscount || (isDomain('nnormal') && !isBlank(voucher))) {
        payload.voucher = voucher;
      }

      const result = await retrieveBag({ payload, context: undefined });

      if (showFreeShipping) {
        updatedPromoLabel = result?.freeShippingLabel || null;
      }
      if (showGiftPurchase) {
        updatedPromoLabel = result?.gwplabel || result?.gwplabelObtained || result?.freeShippingLabel || null;
        if (result?.gwplabelObtained && result?.gwplabelObtained !== '') {
          updatedPromoLabelType = 'obtained';
        }
      }
      if (setDisabledProducts && !isBlank(result.blockedSkus)) {
        setDisabledProducts(result.blockedSkus);
      }

      if (result?.shoppingCart) {
        currentBag = result.shoppingCart;
      }
    }

    if (cookiesMarket !== market) {
      eraseCookie(COOKIES.BAG);
      eraseCookie(COOKIES.BAG_DATE);
      eraseCookie(COOKIES.BAG_MARKET);
      eraseCookie(COOKIES.SHIPMENT);
      eraseCookie(COOKIES.EMPLOYEE_ID);
      eraseCookie(COOKIES.PACKSTATION);
      eraseCookie(COOKIES.IS_CTR);
      eraseCookie(COOKIES.ZIP);
    }

    setBag(currentBag);
    setBagLength(currentBag.length);
    setPromoLabel(updatedPromoLabel);
    setPromoLabelType(updatedPromoLabelType);

    setBagHasMemberOnlyProduct(currentBag.filter((product) => product.membersOnly === true).length > 0);

    dataLayerHandleEvent({
      event: 'cart_sync',
      cart: {
        items: parseBagItemsEcommerceGA4(currentBag),
      },
    });

    return currentBag;
  } catch (e) {
    console.error('Init bag error', e);
    return null;
  }
};

type TI18nContext = {
  t: (parent: string, anchor: string, defaultValue?: string) => string;
  te: (validationKey: ValidationString) => string;
  locale: string;
  countries: any;
  localeConfigs: any;
  profileData: Record<string, any>;
  bagLength: number;
  setBagLength: Dispatch<SetStateAction<number>>;
  bag: any[];
  setBag: Dispatch<SetStateAction<any[]>>;
  bagHasMembersOnlyProduct: boolean;
  setBagHasMemberOnlyProduct: Dispatch<SetStateAction<boolean>>;
  promoLabel: string | null;
  setPromoLabel: Dispatch<SetStateAction<string | null>>;
  promoLabelType: string;
  setPromoLabelType: Dispatch<SetStateAction<string>>;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  searchQueryShadow: string;
  setSearchQueryShadow: Dispatch<SetStateAction<string>>;
  detectedCountry: string | undefined;
  appmode: boolean;
  recommendedProductsForBag: any[];
  setRecommendedProductsForBag: Dispatch<SetStateAction<any[]>>;
  userInteracted: boolean;
  i18nPage: string;
  storeData: Record<string, any> | undefined;
  setStoreData: Dispatch<SetStateAction<Record<string, any> | undefined>>;
  disabledProducts: string;
  unbxdInitialSearch: Record<string, any> | null;
  unbxdSearchResults: Record<string, any> | null;
  setUnbxdSearchResults: Dispatch<SetStateAction<Record<string, any> | null>>;
  giftCardList: string[];
};

export const i18nContext = createContext<TI18nContext | undefined>(undefined);

type I18nProviderProps = Pick<
  TI18nContext,
  | 't'
  | 'appmode'
  | 'storeData'
  | 'setStoreData'
  | 'countries'
  | 'locale'
  | 'profileData'
  | 'detectedCountry'
  | 'userInteracted'
  | 'i18nPage'
  | 'unbxdInitialSearch'
>;

const I18nProvider = ({
  t,
  locale,
  countries,
  children,
  profileData,
  detectedCountry,
  appmode,
  userInteracted,
  i18nPage,
  storeData,
  setStoreData,
  unbxdInitialSearch,
}: PropsWithChildren<I18nProviderProps>) => {
  const [bagLength, setBagLength] = useState(0);
  const [bag, setBag] = useState(null);
  const [bagHasMembersOnlyProduct, setBagHasMemberOnlyProduct] = useState(false);
  const [promoLabel, setPromoLabel] = useState(null);
  const [promoLabelType, setPromoLabelType] = useState('progress');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryShadow, setSearchQueryShadow] = useState('');
  const [disabledProducts, setDisabledProducts] = useState(null);
  // utilizo un queryShadow porque el searchQuery acciona un useEffect en la pagina de /search, asi que tengo que poder distinguirlo para el input en mobile que se puede accionar con el boton de la lupa.
  const [recommendedProductsForBag, setRecommendedProductsForBag] = useLocalStorage('bag-recommended', []);
  const [unbxdSearchResults, setUnbxdSearchResults] = useState(unbxdInitialSearch);

  const wrappedLocaleConfigs = {};

  Object.keys(localeConfigs).forEach((localeConfKey) => {
    wrappedLocaleConfigs[localeConfKey] = localeConfigs[localeConfKey](t);
  });

  useEffect(() => {
    // si la bolsa esta vacia quitar productos recomendados que se hayan quedado
    if (Array.isArray(bag) && bag.length === 0 && recommendedProductsForBag.length > 0) {
      setRecommendedProductsForBag([]);
    }
  }, [bag]);

  useEffect(() => {
    retrieveBagHandler({
      locale,
      config: profileData.config,
      setBag,
      setBagHasMemberOnlyProduct,
      setBagLength,
      setPromoLabel,
      setPromoLabelType,
      setDisabledProducts,
    });
  }, []);

  useEffect(() => {
    setUnbxdSearchResults(unbxdInitialSearch);
  }, [unbxdInitialSearch]);

  const giftCardList = useMemo(() => {
    return (profileData?.giftCardList as string[]) ?? [];
  }, [profileData?.giftCardList]);

  const te = useCallback((validationKey: ValidationString) => {
    let [parent, anchor] = validationKey.split('|');
    if (!parent || !anchor) {
      console.error(`Invalid validation key format. Expected "parent|anchor"; received "${validationKey}"`);
      parent = validationKey;
      anchor = '';
    }
    return t(parent, anchor);
  }, []);

  return (
    <i18nContext.Provider
      value={{
        t,
        locale,
        countries,
        localeConfigs: wrappedLocaleConfigs,
        profileData,
        bagLength,
        setBagLength,
        bag,
        setBag,
        bagHasMembersOnlyProduct,
        setBagHasMemberOnlyProduct,
        promoLabel,
        setPromoLabel,
        promoLabelType,
        setPromoLabelType,
        searchQuery,
        setSearchQuery,
        searchQueryShadow,
        setSearchQueryShadow,
        detectedCountry,
        appmode,
        recommendedProductsForBag,
        setRecommendedProductsForBag,
        userInteracted,
        i18nPage,
        storeData,
        setStoreData,
        disabledProducts,
        unbxdInitialSearch,
        unbxdSearchResults,
        setUnbxdSearchResults,
        giftCardList,
        te,
      }}
    >
      {children}
    </i18nContext.Provider>
  );
};

export function useI18n() {
  const context = useContext(i18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within a I18nProvider');
  }
  return context;
}

I18nProvider.propTypes = {
  children: node,
  locale: string,
  countries: array,
  t: func,
  profileData: object,
  detectedCountry: string,
  appmode: bool,
  additionalAttributes: object,
  userInteracted: bool,
  i18nPage: string,
  storeData: object,
  setStoreData: func,
};

export default I18nProvider;
